/*	Copyright (c) 2015 Jean-Marc VIGLINO, 
  released under the CeCILL-B license (French BSD license)
  (http://www.cecill.info/licences/Licence_CeCILL-B_V1-en.txt).
  
  ol_source_GeoImage is a layer source with georeferencement to place it on a map.
  
  olx.source.GeoImageOptions:
  {	url: {string} url of the static image
    image: {image} the static image, if not provided, use url to load an image
    imageCenter: {ol.Coordinate} of the center of the image
    imageScale: {ol.Size|Number} [scalex, scaley] of the image
    imageRotate: {number} angle of the image in radian, default 0
    imageCrop: {ol.Extent} of the image to be show (in the image) default: [0,0,imageWidth,imageHeight]
    imageMask: {Array.<ol.Coordinate>} - linestring to mask the image on the map
  }
*/

import ol_source_ImageCanvas from "ol/source/ImageCanvas";
import ol_layer_Image from "ol/layer/Image";


/** Inherit the prototype methods from one constructor into another.
 * replace deprecated ol method
 *
 * @param {!Function} childCtor Child constructor.
 * @param {!Function} parentCtor Parent constructor.
 * @function module:ol.inherits
 * @api
 */
const ol_ext_inherits = function(child,parent) {
  child.prototype = Object.create(parent.prototype);
  child.prototype.constructor = child;
};

const ol_source_GeoImage = function (opt_options) {
  const options = {
    attributions: opt_options.attributions,
    logo: opt_options.logo,
    projection: opt_options.projection,
  };

  // options.projection = opt_options.projection;

  this.center = opt_options.center;
  this.latDegree = opt_options.lat;

  // width of the image
  this.width = opt_options.imageWidth;
  // height of the image
  this.height = opt_options.imageHeight;

  // Scale of the image
  this.scale = opt_options.imageScale;
  // Rotation of the image
  this.rotate = opt_options.imageRotate ? opt_options.imageRotate : 0;

  // Load Image
  this._image = (opt_options.image ? opt_options.image : new Image );
  this._image.crossOrigin = opt_options.crossOrigin; // 'anonymous';
  // Show image on load
  const self = this;
  this._image.onload = function() {
    self.showImage (self.crop);
    self.changed();
  };
  if (!opt_options.image) this._image.src = opt_options.url;

  // Draw image on canvas
  options.canvasFunction = function(extent, resolution, pixelRatio, size /*, projection*/ ) {
    const canvas = document.createElement('canvas');
    canvas.width = size[0];
    canvas.height = size[1];
    const ctx = canvas.getContext('2d');


    if (!this._imageSize) return canvas;
    // transform coords to pixel
    function tr(xy) {
      return [
        (xy[0]-extent[0])/(extent[2]-extent[0]) * size[0],
        (xy[1]-extent[3])/(extent[1]-extent[3]) * size[1]
      ];
    }
    // Draw

    const scale = 1 /(resolution*Math.cos(Math.PI/180*this.latDegree)) * pixelRatio;

    const pixel = tr(this.center);

    const dx = (this.width/2) * scale;
    const dy = (this.height/2) * scale;
    const dWidth = this.width * scale;
    const dHeight = this.height * scale;

    ctx.translate(pixel[0],pixel[1]);
    if (this.rotate) {
      ctx.rotate(this.rotate);
    }

    ctx.drawImage(this._image,  -dx, -dy, dWidth, dHeight);
    return canvas;
  };

  ol_source_ImageCanvas.call(this, options);
};

ol_ext_inherits(ol_source_GeoImage, ol_source_ImageCanvas);

/**
 * Show Image.
 * @param {ol.extent|Number} method will set _imageSize from original size.
 * @api stable
 */
ol_source_GeoImage.prototype.showImage = function() {
  // Image not loaded => get it latter
  if (!this._image.naturalWidth) {
    return;
  }
  this._imageSize = [this._image.naturalWidth, this._image.naturalHeight ];
  this.changed();
};

/** Get the extent of the source.
 * @param {module:ol/extent~Extent} extent If provided, no new extent will be created. Instead, that extent's coordinates will be overwritten.
 * @return {ol.extent}
 */
ol_source_GeoImage.prototype.getExtent = function (opt_extent) {
  if (opt_extent) {
    const ext = this.get("extent");
    for (const i = 0; i < opt_extent.length; i++) {
      opt_extent[i] = ext[i];
    }
    return ext;
  } else {
    return this.get("extent");
  }
};

/**
 * @classdesc
 * Image layer to use with a GeoImage source and return the extent calcaulted with this source.
 * @extends {ol.layer.Image}
 * @param {Object=} options Layer Image options.
 * @api
 */
const ol_layer_GeoImage = function (options) {
  ol_layer_Image.call(this, options);
};
ol_ext_inherits(ol_layer_GeoImage, ol_layer_Image);

/**
 * Return the {@link module:ol/extent~Extent extent} of the source associated with the layer.
 * @return {ol.Extent} The layer extent.
 * @observable
 * @api
 */
ol_layer_GeoImage.prototype.getExtent = function () {
  return this.getSource().getExtent();
};

export default {geoImageLayer: ol_layer_GeoImage, 
  geoImageSource: ol_source_GeoImage};
